import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

export default function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const { user, updateProfile, isAuthenticated } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const changeLanguage = async (language: string) => {
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    setCurrentLanguage(language);
    localStorage.setItem('i18nextLng', language);
    setIsOpen(false);

    if (isAuthenticated && user?.language !== language) {
      try {
        await updateProfile({ language });
      } catch {
        // UI language already changed; profile sync failed silently
      }
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        id="language-selector"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="mr-1">
          {currentLanguage === 'en' 
            ? '🇺🇸' 
            : currentLanguage === 'pt-BR' 
              ? '🇧🇷' 
              : '🇪🇸'}
        </span>
        <span className="hidden md:inline">
          {currentLanguage === 'en' 
            ? 'English' 
            : currentLanguage === 'pt-BR' 
              ? 'Português' 
              : 'Español'}
        </span>
        <svg 
          className={`w-4 h-4 ml-1 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-10 w-48 mt-2 origin-top-right bg-white rounded-md shadow-lg">
          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="language-selector">
            <button
              onClick={() => changeLanguage('en')}
              className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left ${
                currentLanguage === 'en' ? 'bg-gray-100' : ''
              }`}
              role="menuitem"
            >
              <span className="mr-2">🇺🇸</span> English
            </button>
            <button
              onClick={() => changeLanguage('pt-BR')}
              className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left ${
                currentLanguage === 'pt-BR' ? 'bg-gray-100' : ''
              }`}
              role="menuitem"
            >
              <span className="mr-2">🇧🇷</span> Português
            </button>
            <button
              onClick={() => changeLanguage('es')}
              className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left ${
                currentLanguage === 'es' ? 'bg-gray-100' : ''
              }`}
              role="menuitem"
            >
              <span className="mr-2">🇪🇸</span> Español
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 